// src/pages/CustomerDetail.js
import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { getCustomerById } from '../services/customerService';
import BackButton from '../components/BackButton';
import formatDateToStandard, { DATE_FORMAT_DISPLAY } from '../utils/DateFormatter';

const CustomerDetail = () => {
  const { orgId, id } = useParams();
  const [customer, setCustomer] = React.useState(null);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    getCustomerById(orgId, id)
      .then(setCustomer)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [orgId, id]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Define main fields
  const mainFields = {
    'Customer ID': customer?.id,
    'Name': customer?.customer_name,
    'Email': customer?.email,
    'Phone': customer?.contact_number,
    'Whatsapp Number': customer?.meta?.whatsapp_number || '',
    // Removed 'Status' field
    'Date Modified': customer ? formatDateToStandard(customer.date_modified) : '',
  };

  // Define additional fields
  const additionalFields = { ...customer };
  Object.keys(mainFields).forEach((key) => {
    delete additionalFields[key];
  });
  delete additionalFields.meta;
  delete additionalFields.audit_log;

  // Add a helper function to parse nested objects and format date fields.
  function parseNestedFields(obj, prefix = '') {
    const result = [];
    Object.entries(obj).forEach(([key, value]) => {
      // Skip null values
      if (value === null) {
        return;
      }
      // Only include specific fields from the organization key
      if (prefix === 'Organization' && !['id', 'organization_name'].includes(key)) {
        return;
      }

      // Skip meta, date_modified, and date_created fields from the organization key
      if (prefix === 'Organization' && /^(meta|date_modified|date_created)$/i.test(key)) {
        return;
      }

      // Capitalize the first letter of each word in the display key
      const displayKey = (prefix
        ? `${prefix} ${key.replace(/_/g, ' ')}`
        : key.replace(/_/g, ' '))
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Recursively parse nested objects
        result.push(...parseNestedFields(value, displayKey));
      } else {
        // Format date strings
        let displayValue = value;
        if (typeof value === 'string' && !isNaN(Date.parse(value))) {
          displayValue = (
            <Tooltip title={`Format: ${DATE_FORMAT_DISPLAY}`}>
              <span>{formatDateToStandard(value)}</span>
            </Tooltip>
          );
        }
        result.push([displayKey, displayValue]);
      }
    });
    console.log("RESULT IS<<<<<<", result)
    return result;
  }

  return (
    <Paper sx={{ p: 3, bgcolor: 'background.paper', color: 'text.primary', backdropFilter: 'saturate(140%) blur(6px)', border: '1px solid', borderColor: 'divider' }}>
      <BackButton />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error ? (
            <Typography variant="h6" color="error">{error}</Typography>
          ) : (
            <>
              <Typography variant="h5" gutterBottom>
                {customer?.customer_name}
              </Typography>

              {/* Main Fields */}
              <Grid container spacing={2}>
                {Object.entries(mainFields).map(([key, value]) => (
                  <React.Fragment key={key}>
                    <Grid item xs={12} sm={3}>
                      <Tooltip title={key === 'Date Modified' ? `Format: ${DATE_FORMAT_DISPLAY}` : ""}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {key === 'Date Modified' ? `${key} (${DATE_FORMAT_DISPLAY})` : key}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Tooltip title={key === 'Date Modified' ? `Format: ${DATE_FORMAT_DISPLAY}` : ""}>
                        <span>{key === 'Date Modified' ? formatDateToStandard(customer.date_modified) : value}</span>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Tooltip title="Copy">
                        <IconButton onClick={() => handleCopy(value)}>
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>

              {/* Detailed Information Grid */}
              {Object.keys(additionalFields).length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>
                    Detailed Information
                  </Typography>
                  <TableContainer component={Paper} sx={{ mt: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Copy</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parseNestedFields(additionalFields).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell>{key}</TableCell>
                            <TableCell>
                              {Array.isArray(value) ? value.join(', ') : (value !== null && value !== undefined ? value.toString() : 'N/A')}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Copy">
                                <IconButton onClick={() => handleCopy(value)}>
                                  <ContentCopy />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </>
      )}
    </Paper>
  );
};

export default CustomerDetail;
